import { memo, useEffect, useMemo, useState, type ReactNode } from "react";
import { Info, Loader2 } from "lucide-react";

import {
  getGrantAffinityProfile,
  isGrantAffinityProfile,
  upsertGrantAffinityProfile,
  type GrantAffinityEntityType,
  type GrantAffinityProfilePayload,
  type GrantAffinityProjectSize,
  type MatchmakerFollowUpQuestion,
} from "@/api/grant_affinity_profile";
import {
  ENTITY_TYPE_OPTIONS,
  PRIORITY_AREA_LABELS,
  PROJECT_SIZE_OPTIONS,
} from "@/components/grant_suite/grantFitDisplay";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/**
 * Tooltip copy for the profile fields (ENG-1692). Must describe what each
 * field really does in scoring — see BuildProfileDocument/GrantFitScoring:
 * only State hard-scopes results, everything else is a soft ranking signal.
 * Exported so tests can pin the honesty of the copy without driving Radix
 * hover in jsdom.
 */
export const PROFILE_FIELD_TIPS = {
  state:
    "Scopes recommendations to federal grants plus regional grants for your state. Setting a Source or State filter on the results overrides this.",
  entityType:
    "Tells the matcher what kind of organization you are, so programs restricted to certain applicant types rank accordingly.",
  priorityAreas:
    "Steer the matcher toward programs in these areas and tailor the follow-up questions. Grants in other areas still appear — this influences ranking, not eligibility.",
  fundingPriorities:
    "The strongest signal in your profile: the matcher compares this description against each grant's mission and eligibility to rank your matches. Specific projects and needs work better than general statements — include target populations too, like low-to-moderate-income (LMI) households, so programs aimed at them rank higher.",
  projectSize:
    "Helps rank programs whose award sizes fit your projects, and picks the follow-up questions that apply to you.",
  disqualifiers:
    "Known reasons a program doesn't work for you, like set-asides you can't use. The matcher ranks those programs lower — it does not exclude them from results.",
} as const;

/**
 * Info icon + tooltip for a profile field. Memoized and provider-less (one
 * TooltipProvider wraps the dialog) so keystroke re-renders stay cheap;
 * portaled so the dialog's overflow doesn't clip the tooltip.
 */
const FieldInfoTip = memo(function FieldInfoTip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="text-muted-foreground transition-colors hover:text-foreground"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent className="max-w-xs">{children}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
});

type GrantAffinityProfileWizardProps = {
  municipalityId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
};

const MIN_PRIORITIES_LENGTH = 20;

export function GrantAffinityProfileWizard({
  municipalityId,
  open,
  onOpenChange,
  onSaved,
}: GrantAffinityProfileWizardProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [priorityAreaOptions, setPriorityAreaOptions] = useState<string[]>(Object.keys(PRIORITY_AREA_LABELS));

  const [organizationName, setOrganizationName] = useState("");
  const [state, setState] = useState("");
  const [entityType, setEntityType] = useState<GrantAffinityEntityType>("city");
  const [priorityAreas, setPriorityAreas] = useState<string[]>([]);
  const [fundingPriorities, setFundingPriorities] = useState("");
  const [typicalProjectSize, setTypicalProjectSize] = useState<GrantAffinityProjectSize>("unknown");
  const [disqualifiers, setDisqualifiers] = useState("");
  const [wizardStep, setWizardStep] = useState<1 | 2>(1);
  const [followUpQuestions, setFollowUpQuestions] = useState<MatchmakerFollowUpQuestion[]>([]);
  const [matchmakerAnswers, setMatchmakerAnswers] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    getGrantAffinityProfile(municipalityId)
      .then((response) => {
        if (cancelled) return;

        if (isGrantAffinityProfile(response)) {
          setOrganizationName(response.organization_name);
          setState(response.state ?? "");
          setEntityType(response.entity_type);
          setPriorityAreas(response.priority_areas);
          setFundingPriorities(response.funding_priorities ?? "");
          setTypicalProjectSize(response.typical_project_size);
          setDisqualifiers(response.disqualifiers ?? "");
          setMatchmakerAnswers(response.matchmaker_answers ?? {});
          setFollowUpQuestions(response.follow_up_questions ?? []);
          setPriorityAreaOptions(response.priority_area_options);
          setWizardStep(1);
          return;
        }

        setOrganizationName(response.prefill.organization_name);
        setState(response.prefill.state ?? "");
        setEntityType(response.prefill.entity_type);
        setPriorityAreas([]);
        setFundingPriorities("");
        setTypicalProjectSize("unknown");
        setDisqualifiers("");
        setMatchmakerAnswers({});
        setFollowUpQuestions([]);
        setWizardStep(1);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Could not load profile");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, municipalityId]);

  const canSave = useMemo(
    () =>
      organizationName.trim().length > 0 &&
      priorityAreas.length > 0 &&
      fundingPriorities.trim().length >= MIN_PRIORITIES_LENGTH,
    [organizationName, priorityAreas, fundingPriorities],
  );

  const togglePriorityArea = (area: string) => {
    setPriorityAreas((prev) =>
      prev.includes(area) ? prev.filter((value) => value !== area) : [...prev, area],
    );
  };

  const buildPayload = (): GrantAffinityProfilePayload => ({
    organization_name: organizationName.trim(),
    state: state.trim() || null,
    entity_type: entityType,
    priority_areas: priorityAreas,
    funding_priorities: fundingPriorities.trim(),
    typical_project_size: typicalProjectSize,
    disqualifiers: disqualifiers.trim() || null,
    matchmaker_answers: matchmakerAnswers,
  });

  const handleContinue = async () => {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved = await upsertGrantAffinityProfile(municipalityId, buildPayload());
      const pending = saved.follow_up_questions ?? [];
      if (pending.length > 0) {
        setFollowUpQuestions(pending);
        setWizardStep(2);
      } else {
        onSaved();
        onOpenChange(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const followUpsComplete = useMemo(
    () =>
      followUpQuestions.every((question) => {
        const answer = matchmakerAnswers[question.id]?.trim() ?? "";
        if (question.input_type === "boolean") {
          return answer === "yes" || answer === "no";
        }
        return answer.length > 0;
      }),
    [followUpQuestions, matchmakerAnswers],
  );

  const handleSave = async () => {
    if (!followUpsComplete) return;
    setSaving(true);
    setError(null);

    try {
      await upsertGrantAffinityProfile(municipalityId, buildPayload());
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const setAnswer = (questionId: string, value: string) => {
    setMatchmakerAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="ordinal-scrollbar max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* One provider for all field tips; per-tip providers measurably slow
            keystroke re-renders of this dialog. */}
        <TooltipProvider delayDuration={300}>
        <DialogHeader>
          <DialogTitle>Grant affinity profile</DialogTitle>
          <DialogDescription>
            {wizardStep === 1
              ? "Tell us about your organization so we can recommend grants that match your priorities and eligibility."
              : "A few follow-up questions help tune your matchmaker recommendations."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {wizardStep === 1 ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="affinity-org-name">Organization name</Label>
                    <Input
                      id="affinity-org-name"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label htmlFor="affinity-state">State</Label>
                      <FieldInfoTip label="About state">{PROFILE_FIELD_TIPS.state}</FieldInfoTip>
                    </div>
                    <Input
                      id="affinity-state"
                      value={state}
                      maxLength={2}
                      onChange={(e) => setState(e.target.value.toUpperCase())}
                      placeholder="AR"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label>Organization type</Label>
                      <FieldInfoTip label="About organization type">{PROFILE_FIELD_TIPS.entityType}</FieldInfoTip>
                    </div>
                    <Select value={entityType} onValueChange={(value) => setEntityType(value as GrantAffinityEntityType)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENTITY_TYPE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label>Priority areas</Label>
                    <FieldInfoTip label="About priority areas">{PROFILE_FIELD_TIPS.priorityAreas}</FieldInfoTip>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {priorityAreaOptions.map((area) => {
                      const selected = priorityAreas.includes(area);
                      return (
                        <Button
                          key={area}
                          type="button"
                          size="sm"
                          variant={selected ? "default" : "outline"}
                          className={cn("h-8 text-xs")}
                          onClick={() => togglePriorityArea(area)}
                        >
                          {PRIORITY_AREA_LABELS[area] ?? area}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="affinity-priorities">What are you trying to fund right now?</Label>
                    <FieldInfoTip label="About funding priorities">{PROFILE_FIELD_TIPS.fundingPriorities}</FieldInfoTip>
                  </div>
                  <Textarea
                    id="affinity-priorities"
                    value={fundingPriorities}
                    onChange={(e) => setFundingPriorities(e.target.value)}
                    rows={4}
                    placeholder="Describe current projects, funding goals, and community needs..."
                  />
                  <p className="text-xs text-muted-foreground">
                    At least {MIN_PRIORITIES_LENGTH} characters.
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label>Typical project size</Label>
                    <FieldInfoTip label="About typical project size">{PROFILE_FIELD_TIPS.projectSize}</FieldInfoTip>
                  </div>
                  <Select
                    value={typicalProjectSize}
                    onValueChange={(value) => setTypicalProjectSize(value as GrantAffinityProjectSize)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PROJECT_SIZE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="affinity-disqualifiers">Common disqualifiers (optional)</Label>
                    <FieldInfoTip label="About common disqualifiers">{PROFILE_FIELD_TIPS.disqualifiers}</FieldInfoTip>
                  </div>
                  <Textarea
                    id="affinity-disqualifiers"
                    value={disqualifiers}
                    onChange={(e) => setDisqualifiers(e.target.value)}
                    rows={2}
                    placeholder="e.g. Not eligible for tribal set-aside programs"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {!followUpsComplete ? (
                  <p className="text-sm text-muted-foreground">Answer all follow-up questions to finish your profile.</p>
                ) : null}
                {followUpQuestions.map((question) => (
                  <div key={question.id} className="space-y-2">
                    <Label>{question.prompt}</Label>
                    {question.input_type === "boolean" ? (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant={matchmakerAnswers[question.id] === "yes" ? "default" : "outline"}
                          onClick={() => setAnswer(question.id, "yes")}
                        >
                          Yes
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={matchmakerAnswers[question.id] === "no" ? "default" : "outline"}
                          onClick={() => setAnswer(question.id, "no")}
                        >
                          No
                        </Button>
                      </div>
                    ) : question.input_type === "select" && question.options ? (
                      <Select
                        value={matchmakerAnswers[question.id] || ""}
                        onValueChange={(value) => setAnswer(question.id, value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select one" />
                        </SelectTrigger>
                        <SelectContent>
                          {question.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Textarea
                        value={matchmakerAnswers[question.id] || ""}
                        onChange={(e) => setAnswer(question.id, e.target.value)}
                        rows={2}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          {wizardStep === 2 ? (
            <Button type="button" variant="outline" onClick={() => setWizardStep(1)} disabled={saving}>
              Back
            </Button>
          ) : null}
          {wizardStep === 1 ? (
            <Button type="button" onClick={handleContinue} disabled={!canSave || saving || loading}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continue
            </Button>
          ) : (
            <Button type="button" onClick={handleSave} disabled={!followUpsComplete || saving || loading}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Save profile
            </Button>
          )}
        </DialogFooter>
        </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
